import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@/components/ui/visually-hidden';

import { useDarkMode } from '@/lib/hooks/use-dark-mode';

interface ContextHeroProps {
    eyebrow: string;
    headline?: string;
    subtitle?: string;
    image?: string;
    metadata?: {
        place?: string;
        era?: string;
        date?: string;
        genre?: string;
    };
    onClose: () => void;
    variant?: 'standard' | 'slim' | 'tiny';
    showHandle?: boolean;
    showClose?: boolean;
    className?: string;
    children?: React.ReactNode;
    lockBackground?: boolean;
    backgroundOffset?: string;
}

/**
 * ContextHero provides a unified, cinematic header for context pages.
 * It integrates the title hierarchy, navigation actions, and handle as overlays.
 */
export function ContextHero({
    eyebrow,
    headline,
    subtitle,
    image,
    metadata,
    onClose,
    variant = 'standard',
    showHandle = false, // Default to false to avoid double handles in panels
    showClose = true,
    className,
    children,
    lockBackground = false,
    backgroundOffset
}: ContextHeroProps) {
    const isSlim = variant === 'slim';
    const isTiny = variant === 'tiny';
    const isDarkMode = useDarkMode();
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    // Is it a "real" image (from storage) vs a bundled illustration asset?
    const isRealImage = image && (image.startsWith('http') || image.startsWith('https'));

    // Dynamic styles based on content
    const hasImage = !!image;

    const containerClasses = hasImage
        ? "bg-black text-white"
        : className?.includes('bg-transparent')
            ? "text-white drop-shadow-md"
            : isDarkMode
                ? "bg-black text-white"
                : "bg-white text-foreground border-b-2";

    return (
        <section
            className={cn(
                "relative overflow-hidden rounded-t-[2.5rem]",
                containerClasses,
                // Height adjustments
                lockBackground && "min-h-[340px] sm:min-h-[400px]",
                !children && (
                    hasImage
                        ? (isTiny ? "min-h-[70px]" : isSlim ? "min-h-[140px] sm:min-h-[110px]" : "min-h-[180px]")
                        : (headline ? "min-h-[auto]" : "min-h-[60px]") // Tiny mode if no headline
                ),
                hasImage && "border-b border-border/40",
                !hasImage && "border-border/10",
                className
            )}
        >
            {/* 1. Drag Handle (Overlay) - Visual Only */}
            {showHandle && (
                <div className="absolute top-0 left-0 right-0 z-30 flex justify-center pt-1 pb-1">
                    <div className={cn(
                        "w-12 h-1 rounded-full transition-colors backdrop-blur-md shadow-[0_2px_10px_rgba(0,0,0,0.3)]",
                        hasImage || className?.includes('bg-transparent')
                            ? "bg-white/60 hover:bg-white/80"
                            : (isDarkMode ? "bg-white/20" : "bg-black/30")
                    )} />
                </div>
            )}

            {/* 2. Background Image & Dark Gradient */}
            {hasImage && (
                <div
                    className={cn(
                        "absolute z-0 bg-black",
                        lockBackground ? "top-0 left-0 right-0 bottom-0 min-h-[1000px] pointer-events-none" : "inset-0",
                        isRealImage && !lockBackground && "cursor-zoom-in"
                    )}
                    onClick={() => isRealImage && !lockBackground && setIsImageModalOpen(true)}
                >
                    <img
                        src={image}
                        alt=""
                        className={cn(
                            "w-full h-full object-cover saturate-[0.8] contrast-[1.05] opacity-75",
                            lockBackground && !backgroundOffset && "object-top"
                        )}
                        style={{
                            objectPosition: backgroundOffset
                        }}
                    />
                    {/* Text protection gradients - Stronger for better legibility */}
                    <div className={cn(
                        "absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20",
                        lockBackground ? "opacity-100" : "opacity-80"
                    )} />
                    <div className={cn(
                        "absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent",
                        lockBackground ? "opacity-90" : "opacity-60"
                    )} />
                </div>
            )}

            {/* 3. Navigation Overlays (Top Right) */}
            {showClose && (
                <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                            "h-10 w-10 rounded-full backdrop-blur-md transition-all",
                            hasImage
                                ? "bg-white/30 border border-white/40 hover:bg-white/50 text-white shadow-[0_4px_12px_rgba(0,0,0,0.5)]"
                                : (isDarkMode
                                    ? "bg-white/10 hover:bg-white/20 !text-white border border-transparent"
                                    : "bg-black/5 hover:bg-black/10 !text-black border border-transparent"
                                )
                        )}
                        onClick={onClose}
                        title="Close"
                    >
                        <X className="h-5 w-5" />
                    </Button>
                </div>
            )}

            <div className={cn(
                "relative z-10 flex flex-col justify-center",
                isTiny ? "px-3 py-2 md:px-5 min-h-[40px]" : isSlim ? "p-5 pb-4 min-h-[140px] sm:min-h-[110px]" : "p-6 pb-2 md:p-10 md:pb-4 min-h-[180px]",
                lockBackground && "pt-12" // Room for the handle if locked
            )}>
                <div className="flex flex-col gap-1 md:gap-2">
                    {/* Eyebrow */}
                    <div>
                        <span className={cn(
                            "font-bold uppercase tracking-[0.2em] leading-none",
                            hasImage || className?.includes('bg-transparent')
                                ? "text-white drop-shadow-md"
                                : (isDarkMode ? "text-white/60" : "text-foreground/50"),
                            isTiny ? "inline-block text-[10px] md:text-xs tracking-[0.15em]" : "inline-block text-[10px] md:text-xs"
                        )}>
                            {eyebrow}
                        </span>
                    </div>

                    {/* Headline */}
                    {headline && (
                        <h1
                            className={cn(
                                "font-serif tracking-tight leading-tight",
                                isTiny ? "text-base md:text-xl" : "text-2xl md:text-4xl [text-shadow:_0_1px_8px_rgba(0,0,0,0.5)]",
                                hasImage || className?.includes('bg-transparent') ? "text-white" : (isDarkMode ? "text-white" : "text-foreground")
                            )}
                        >
                            {headline}
                        </h1>
                    )}

                    {subtitle && (
                        <p className={cn(
                            "font-sans text-white/95 max-w-3xl leading-relaxed mt-1.5 md:mt-1 shadow-sm",
                            isSlim ? "text-xs md:text-sm line-clamp-2 font-medium md:font-normal" : "text-sm md:text-lg line-clamp-2 md:line-clamp-none font-medium md:font-normal"
                        )}>
                            {subtitle}
                        </p>
                    )}

                    {/* Metadata */}
                    {metadata && (metadata.place || metadata.era || metadata.date || metadata.genre) && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 md:mt-0 md:absolute md:bottom-8 md:right-10 md:z-40">
                            {metadata.genre && (
                                <div className="flex items-center gap-2">
                                    <Badge variant="secondary" className="bg-white/10 text-white border-white/20 backdrop-blur-md uppercase tracking-widest text-[10px]">
                                        {metadata.genre}
                                    </Badge>
                                </div>
                            )}
                            {metadata.date && (
                                <div className="flex items-center gap-2">
                                    <div className="w-1 h-1 rounded-full bg-primary/40" />
                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
                                        {metadata.date}
                                    </span>
                                </div>
                            )}
                            {metadata.place && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsImageModalOpen(true);
                                    }}
                                    className="group/meta flex items-center gap-2 hover:opacity-100 transition-all text-left"
                                >
                                    <div className="w-1 h-1 rounded-full bg-primary/40 md:hidden" />
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 border-b border-transparent group-hover/meta:text-white group-hover/meta:border-white/40 transition-all flex items-center gap-2 py-0.5">
                                        {metadata.place}
                                        <Maximize2 className="h-3 w-3 opacity-0 group-hover/meta:opacity-60 transition-opacity" />
                                    </span>
                                </button>
                            )}
                            {metadata.era && (
                                <div className="flex items-center gap-2">
                                    <div className="w-1 h-1 rounded-full bg-primary/40 md:hidden" />
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">
                                        {metadata.era}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Sub-content (Children) */}
                {children && (
                    <div className={cn(
                        "w-full",
                        isTiny ? "mt-0" : "mt-4 sm:mt-8"
                    )}>
                        {children}
                    </div>
                )}
            </div>

            {/* Image Detail Modal */}
            <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
                <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 border-none bg-black/95 backdrop-blur-3xl overflow-hidden">
                    <VisuallyHidden>
                        <DialogTitle>Full High-Resolution Image of {metadata?.place || headline}</DialogTitle>
                    </VisuallyHidden>
                    <div className="relative w-full h-full flex items-center justify-center p-4">
                        <img
                            src={image}
                            alt={metadata?.place || headline}
                            className="w-full h-full object-contain rounded-lg shadow-2xl"
                        />

                        {/* Modal Metadata Overlay */}
                        <div className="absolute bottom-6 left-6 right-6 flex flex-col gap-2">
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">{eyebrow}</span>
                                <div className="h-px w-8 bg-white/20" />
                            </div>
                            <h3 className="text-3xl font-serif font-bold text-white/95">{metadata?.place || headline}</h3>
                            {metadata?.era && <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">{metadata.era}</span>}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
}
